// menschenFuersUnternehmenService.ts — persistiert „Menschen fürs Unternehmen"
// (GF/Gesellschafter + Verantwortlichkeiten) der Firmen-Anlage / ContactDetails
// über den transaktionalen Backend-Endpoint (Status + FO/BO-Vererbung server-seitig).

import { apiUrl } from "../utils/api";
import type { MenschItem } from "../components/verantwortlichkeit/buildMenschenItems";

/** Frisch angelegte Person aus dem attach-menschen-Batch (Mail→Vorgang SP4). */
export type CreatedPerson = { newPersonKey: string | null; contactId: number };

/**
 * Hängt die gebauten Items an. Server legt neue Personen/externe Firmen an, erbt
 * FO/BO der Firma, setzt Status „Verantwortung fürs Unternehmen" und die Beziehungen.
 * Wirft bei HTTP-Fehler (Aufrufer entscheidet über Fehlerbehandlung).
 * Liefert die im Batch NEU angelegten Personen (id + newPersonKey) zurück —
 * SP4 braucht die frische id, um den neuen AP direkt als Vorgang-Zuweisung zu
 * übernehmen. Bestehende Aufrufer ignorieren den Rückgabewert.
 */
export async function attachMenschen(
  items: MenschItem[],
  replace = false,
): Promise<CreatedPerson[]> {
  if (!items.length) return [];
  const res = await fetch(apiUrl("ansprechpartner-cleanup/attach-menschen"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // replace=true (Detail-Edit): ersetzt die bestehenden Verantwortlichkeiten des
    // Paars server-seitig transaktional; Default false = additiv (Anlage).
    body: JSON.stringify({ items, replace }),
  });
  if (!res.ok) {
    throw new Error(`attach-menschen fehlgeschlagen (${res.status})`);
  }
  const body = await res.json().catch(() => null);
  return Array.isArray(body?.createdPersons) ? (body.createdPersons as CreatedPerson[]) : [];
}

/**
 * Legt einen Vorgang vom Typ NKG (Neukundengewinnung) für die Firma an — ohne
 * Frist, kein Todo. Der Betreff IST die Art (PO 28.07.2026): „NKG". Das Backend
 * erzwingt das ohnehin (applyBetreffRegel), der Parameter bleibt nur für
 * Aufrufer mit abweichender Art bestehen.
 */
export async function createNkgVorgang(companyId: number, titel = "NKG"): Promise<void> {
  await fetch(apiUrl("vorgang"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kunde: { id: companyId }, titel, vorgangsart: "NKG", frist: null }),
  });
}
